// Rótulo do único caminho de saída da visão imersiva. Constante porque o
// atalho de teclado e a view precisam concordar sobre a mesma ação, e porque
// texto que vai à tela não vive solto no template (regra do repo).
export const VOLTAR_PARA_GESTAO = 'Voltar para Gestão'

// Nome da rota imersiva. Deliberadamente SEM o prefixo `aawz-intelligence.`:
// três mecanismos do repo casam por prefixo de nome de rota (dedup de módulo na
// sidebar v2, aba ativa da nav-bar, e o header resolvido do ConditionalTeleport),
// e os três quebrariam em silêncio. Ver o comentário na rota, em
// `modules/aawz-intelligence/routes.js`.
export const ROTA_IMERSIVA = 'ia-imersivo'

// A MESMA tela, endereçada por conversa (`/home-ia/c/<uuid>`) — decisão do
// owner, 18/09/2026: *"até para que cada conversa consiga ter um url direto
// para ela"*.
//
// Nome DERIVADO do da rota mãe, e isso é deliberado: os três mecanismos que
// casam por PREFIXO de nome de rota (dedup de módulo na sidebar v2, aba ativa
// da nav-bar e o header do ConditionalTeleport) passam a tratar a conversa como
// a mesma tela — que é o que ela é. O único casamento por IGUALDADE EXATA é o
// de `ROTAS_SEM_WIDGET_CHAT_IA` (`global/utils/chat-ia-widget-visivel.js`), e
// por isso este nome está declarado LÁ também; sem aquela linha, o FAB do chat
// volta a flutuar por cima da tela cheia.
export const ROTA_IMERSIVA_CONVERSA = 'ia-imersivo-conversa'

// Nome do parâmetro de path que carrega o id da conversa. Constante porque a
// rota, a view e o teste precisam concordar sobre a mesma chave.
export const PARAM_ID_CONVERSA = 'idConversa'

// Destino de fallback do "Voltar" quando não há rota de origem — aba nova, link
// direto ou F5. Mesma rota que a navegação 2026 usa como porta de entrada da
// gestão (`GESTAO_GATEWAY_ROUTE`), então o rótulo continua verdadeiro.
export const ROTA_GESTAO_PADRAO = 'clientes'

// Perfil do cliente — destino do "Ver perfil" do cartão de escopo. Mesma rota
// que a página de Clientes e a busca global usam depois de `enterClient`.
export const ROTA_PERFIL_CLIENTE = 'cliente-perfil'

// Prefixo do `console.warn` quando `empurrarRota` REJEITA (rota sem `href`):
// é defeito de configuração de rota, não de pop-up — precisa aparecer no
// console em vez de morrer num `catch` vazio.
export const AVISO_ROTA_SEM_HREF = '[chat-imersivo] Não foi possível abrir a tela em nova aba:'

// Destino da faixa do composer: a fila de curadoria.
//
// Mesmo gate do `/ia` (`FEATURES.CHAT_IA` via `customPermission`), então quem
// está aqui já pode chegar lá — não precisa de verificação extra. E é a tela
// que mostra a MESMA contagem que a faixa exibe, sob o rótulo "A triar".
export const ROTA_SUGESTOES_A_TRIAR = 'aawz-intelligence.curadoria'

// Que forma o estado vazio oferece abaixo do composer.
//
// ★ Histórico da ida-e-volta (fechado em 10/09/2026 — decisão DEFINITIVA,
// não mais um teste A/B):
//
// 1. Primeira versão: catálogo COMPLETO de módulos, em CARTÃO QUADRADO,
//    sempre visível — reprovada pelo Filipe (CTO): "aquelas sugestões... tu
//    tinha feito de um jeito, por módulo, que o Filipe não gostou".
// 2. Segunda versão (06/09): 3 sugestões GENÉRICAS de exemplo (client-side,
//    sem chamada ao backend), alternando entre estilo LISTA (ícone + texto,
//    "estilo ChatGPT") e o mesmo cartão quadrado de antes — um teste A/B via
//    `MODOS_DE_SUGESTAO`/`MODO_DE_SUGESTAO`/`SUGESTOES_FIXAS_DE_EXEMPLO`, que
//    existiram aqui e foram RETIRADOS nesta mudança.
// 3. Terceira versão, e a atual (10/09/2026, pedido explícito do usuário):
//    volta ao CATÁLOGO REAL de módulos do escritório — a MESMA fonte do menu
//    "+" do composer (`ferramentasDoComposer`,
//    `view-chat-imersivo.vue`) —, mas em estilo LISTA (nunca mais cartão
//    quadrado), com Fee Fixo somado ao catálogo como PRIMEIRA opção
//    (`constants/acoes-por-modulo.js`) e com filtro AO VIVO pelo texto
//    digitado no composer (`acoesPorModuloFiltradas`, `views/chat/view-chat.vue`).
//    O clique numa linha SELECIONA o módulo (mesmo efeito do "+"), não envia
//    uma pergunta — ver `unidade: 'modulo'` em
//    `components/acoes-por-modulo/acoes-por-modulo.vue`.
//
// Quanto espaço a grade de ações ocupa.
//
// `AMPLA` é o desenho da primeira versão: cartão branco com borda, ícone de
// 40px, descrição sem teto de linhas. `COMPACTA` recua a superfície (branco só
// no hover) e encolhe tudo — foi o que devolveu o chat ao centro depois do
// retorno de 26/08, já que a coluna do estado vazio é `justify-center`
// e cada linha de grade a mais empurra o composer para cima.
//
// Duas constantes e não uma: alternar o MODO troca grade por pílulas; alternar
// a DENSIDADE troca o tamanho da grade. São perguntas diferentes.
export const DENSIDADES_DA_GRADE = Object.freeze({
  COMPACTA: 'compacta',
  AMPLA: 'ampla',
})

export const DENSIDADE_DA_GRADE = DENSIDADES_DA_GRADE.COMPACTA

// Como as ações do módulo ESCOLHIDO no "+" aparecem abaixo do composer.
//
// `CARROSSEL` é o desenho da primeira versão: cabeçalho com o nome do módulo,
// chevrons e uma fileira de cartões que rola na horizontal. `LISTA` troca por
// uma ação por linha, título e descrição no mesmo eixo, sem cabeçalho — o
// módulo já está no chip da caixa, e repeti-lo era dizer a mesma coisa duas
// vezes. Trocar o valor abaixo alterna as duas sem tocar em componente.
//
// ★ Eixo diferente da lista fixa de módulos do estado vazio
// (`acoesPorModulo`, acima): aquela decide o que a tela mostra quando NADA
// foi escolhido; este, como ela responde depois da escolha.
//
// ⚠️ As duas seguram a ALTURA, por caminhos diferentes: a coluna do estado
// vazio é `justify-center`, e o que crescer aqui empurra o composer para cima.
// O carrossel rola na horizontal; a lista tem `max-height` e rola na vertical.
// Nenhuma das duas corta ação — o CRM tem oito e todas continuam alcançáveis.
export const LAYOUTS_DO_MODULO_EM_FOCO = Object.freeze({
  CARROSSEL: 'carrossel',
  LISTA: 'lista',
})

export const LAYOUT_DO_MODULO_EM_FOCO = LAYOUTS_DO_MODULO_EM_FOCO.LISTA

// O que a tela em repouso mostra abaixo do composer.
//
// `COMPLETO` é a tela de hoje: grade de ações por módulo, painel do dia
// (Próximos eventos + Tarefas prioritárias) e a faixa de "N sugestões a triar"
// dentro da caixa de texto. `LIMPO` apaga os três e deixa só o herói e o
// composer.
//
// ★ Esta constante é de OUTRA camada que as duas acima. Ela decide se o bloco
// EXISTE; a lista fixa de módulos (`acoesPorModulo`) e `DENSIDADE_DA_GRADE`
// decidem como ele aparece quando existe. Em `LIMPO` as duas ficam sem
// efeito — não são três interruptores concorrentes.
export const MODOS_DA_PAGINA = Object.freeze({
  COMPLETO: 'completo',
  LIMPO: 'limpo',
})

export const MODO_DA_PAGINA = MODOS_DA_PAGINA.LIMPO

// Se o composer aceita anexo.
//
// [feedback do usuário, 04/09] Ligado de propósito ANTES do backend — decisão
// do usuário, sabendo do gap: "libera que o John vai fazer [o backend], esse
// código não vai para produção no momento". Então isto não é engano nem
// esquecimento do gate original — é o próprio uso que o gate previa.
//
// ★ [spec-016 T023] O `POST /chat` AGORA TEM campo de arquivo — `_buildChatBody`
// leva `anexos: [{upload_id, nome, content_type, tamanho}]` quando o upload
// (via `send-message-anexos-upload-mixin.js`) termina com sucesso. Mas o
// canal de upload real é fortemente acoplado ao domínio de carteira do Fee
// Fixo (`POST /fee-fixo/clientes/carteira-chat/upload`, exige cliente em
// foco ANTES de aceitar o arquivo) — não é um upload genérico para qualquer
// anexo do chat. Por isso a faixa de aviso (`avisoDeAnexo`) continua na tela:
// ela só aparece quando SOBRA algum anexo sem `upload_id` (sem cliente em
// foco, ou upload que falhou) — não mais incondicionalmente.
//
// ⚠️ E a imagem que aparece na bolha depois de enviar continua sendo ECO
// LOCAL: some num F5, porque o `GET /messages` não devolve anexo (persistir
// o que o modelo LEU do upload_id é escopo do backend, fora deste front). Ver
// `anexosParaExibir` em `api/apiChat.js`, que de propósito NÃO entra no
// corpo do POST — é `anexos` (campo irmão, uploads já concluídos) quem entra.
//
// ★ [spec-003 T306, 19/09/2026] A DÍVIDA FOI QUITADA. O comentário que estava
// aqui dizia "fica AQUI, e não numa flag de escritório: é estado de
// desenvolvimento, não de produto" — e a US-1/US-11 da spec-003 pede
// exatamente o contrário: "WHERE a funcionalidade `IA - Análise Agêntica` está
// desligada no escritório THE system SHALL ocultar o botão de anexo".
//
// A régua agora é `utils/anexos-liberados.js::anexosLiberados($access)`, com
// override de desenvolvimento fora de produção. Continua descendo por PROP:
// `default: false` no `send-message` mantém popup, rota cheia, painel do Hub e
// Fee-Based exatamente como estão — nenhum deles passa a prop.
